import { Tooltip } from "@octopusdeploy/design-system-components";
import { LiveStatus } from "@octopusdeploy/design-system-octopus-components";
import type { LiveStatusProps, LiveStatusType } from "@octopusdeploy/design-system-octopus-components";
import type { KubernetesLiveApplicationStatusCode } from "@octopusdeploy/octopus-server-client";
import { useOctopusFeatureToggle } from "~/hooks/useOctopusFeatureToggle";
import * as LiveAppStatusIcon from "./LiveAppIcons";
interface LiveStatusIconProps {
    status: KubernetesLiveApplicationStatusCode;
    size: LiveAppStatusIcon.LiveStatusIconSize;
}
function getTooltipText(status: KubernetesLiveApplicationStatusCode): string {
    switch (status) {
        case "OutOfSync":
            return "Desired state on the cluster is different from the desired state applied";
        case "Progressing":
            return "Some objects are in a progressing state";
        case "Degraded":
            return "Desired state not achieved and the application has encountered issues";
        case "Missing":
            return "The application has missing objects";
        case "Healthy":
            return "The application's desired state was achieved";
        case "Unavailable":
            return "Application live status is unavailable because your last deployment failed";
        case "Waiting":
            return "A deployment is in progress";
        case "Stale":
            return "The live status information is stale. No data has been received in the last 10 minutes.";
        case "Unknown":
        default:
            return "The application has objects in an unknown state.";
    }
}
function toLiveStatusType(status: KubernetesLiveApplicationStatusCode): LiveStatusType {
    switch (status) {
        case "Healthy":
            return "healthy";
        case "Progressing":
            return "progressing";
        case "Degraded":
            return "degraded";
        case "Missing":
            return "missing";
        case "Unavailable":
            return "status-unavailable";
        case "Waiting":
            return "waiting";
        case "Stale":
            return "stale";
        case "OutOfSync":
            return "out-of-sync";
        case "Unknown":
        default:
            return "unknown";
    }
}
function toBaseStatusSize(size: LiveAppStatusIcon.LiveStatusIconSize): LiveStatusProps["size"] {
    switch (size) {
        case 20:
            return 28;
        case 28:
            return 28;
        case 40:
            return 40;
        case 48:
            return 40;
    }
}
const LiveStatusToVisuals = (status: KubernetesLiveApplicationStatusCode, size: LiveAppStatusIcon.LiveStatusIconSize): JSX.Element => {
    const tooltip = getTooltipText(status);
    switch (status) {
        case "OutOfSync":
            return <Tooltip content={tooltip}>{LiveAppStatusIcon.OutOfSyncIcon(size)}</Tooltip>;
        case "Progressing":
            return <Tooltip content={tooltip}>{LiveAppStatusIcon.ProgressingIcon(size)}</Tooltip>;
        case "Degraded":
            return <Tooltip content={tooltip}>{LiveAppStatusIcon.DegradedIcon(size)}</Tooltip>;
        case "Missing":
            return <Tooltip content={tooltip}>{LiveAppStatusIcon.MissingIcon(size)}</Tooltip>;
        case "Healthy":
            return <Tooltip content={tooltip}>{LiveAppStatusIcon.HealthyIcon(size)}</Tooltip>;
        case "Unavailable":
            return <Tooltip content={tooltip}>{LiveAppStatusIcon.UnavailableIcon(size)}</Tooltip>;
        case "Waiting":
            return <Tooltip content={tooltip}>{LiveAppStatusIcon.WaitingIcon(size)}</Tooltip>;
        case "Stale":
            return <Tooltip content={tooltip}>{LiveAppStatusIcon.StaleIcon(size)}</Tooltip>;
        case "Unknown":
        default:
            return <Tooltip content={tooltip}>{LiveAppStatusIcon.UnknownIcon(size)}</Tooltip>;
    }
};
export const LiveStatusIcon = (props: LiveStatusIconProps) => {
    const newStatusIconsEnabled = useOctopusFeatureToggle("new-status-icons", false);
    if (props.status === "NotSupported") {
        return <></>;
    }
    if (!newStatusIconsEnabled) {
        return LiveStatusToVisuals(props.status, props.size);
    }
    return <LiveStatus status={toLiveStatusType(props.status)} size={toBaseStatusSize(props.size)} tooltip={getTooltipText(props.status)}/>;
};
